import { createSlice, createSelector, createEntityAdapter, createAction, createAsyncThunk } from "@reduxjs/toolkit";
import { simpleSetPayload } from "../constants";
import { sortByFieldAlphabetically } from "@amplio-common/funcs/commonFunctionalities";
import { convertStudentMessagesToRequiredFormat, CHAT_WIDGET_STATUS as CWS } from "@app/routes/treatmentV3/components/chat/ChatUtils";
import { updateChatReadMessages } from "@amplio-common/treatments/api";
import { SLICE_NAMES } from "./sliceNames";

export const sendChatMessage = createAction(`${SLICE_NAMES.CHAT_SLICE}/sendChatMessage`);
export const notifyOpenChatWidget = createAction(`${SLICE_NAMES.CHAT_SLICE}/notifyOpenChatWidget`);

const chatSlice_STATE_ROOT_SELECTOR = state => state[SLICE_NAMES.CHAT_SLICE];

const createSimpleSelector = (fieldName) => createSelector(
  [state => state[SLICE_NAMES.CHAT_SLICE][fieldName]], field => field,
);

const sortFunc = sortByFieldAlphabetically("name");

export const chatStudentsAdapter = createEntityAdapter({
  sortComparer: sortFunc,
});

export const chatMapTSToStudentStatus = (ts) => ({
  id: ts.student.id,
  name: ts.student.name,
  firstName: ts.student.first_name,
  messageList: convertStudentMessagesToRequiredFormat(ts.chat_messages || [], ts.student.id),
  numUnreadMessages: ts?.chat_messages?.filter(msg => msg.message_read_by_other_side === false && msg.sent_to !== ts.student.id)?.length || 0,
  chatWidgetStatus: CWS.CLOSED,
  messageDraft: "",
});

const chatStudentsAdapterSelectors = chatStudentsAdapter.getSelectors(chatSlice_STATE_ROOT_SELECTOR);

const initialState = {
  messageList: [],
  numUnreadMessages: 0,
  messageDraft: "",
  chatWidgetStatus: CWS.CLOSED,
  ...chatStudentsAdapter.getInitialState(),
};

export const setChatWidgetStatusAndUpdateReadTime = createAsyncThunk(
  `${SLICE_NAMES.CHAT_SLICE}/setChatWidgetStatusAndUpdateReadTime`,
  async (payload) => {
    const { sentTo, sentBy, treatmentId, chatWidgetStatus } = payload;

    const data = {
      sent_to: sentTo,
      sent_by: sentBy,
    };
          
    updateChatReadMessages(treatmentId, data);
    return chatWidgetStatus;
  },
);

export const chatSlice = createSlice({
  name: SLICE_NAMES.CHAT_SLICE,
  initialState,
  reducers: {
    setChatMessageHandler: simpleSetPayload("chatMessageHandler"),
    setMessageList: simpleSetPayload("messageList"),
    setInitialMessageList: simpleSetPayload("messageList"), //different action for intial load to avoid an unwanted event
    setNumUnreadMessages: simpleSetPayload("numUnreadMessages"),
    setMessageDraft: simpleSetPayload("messageDraft"),
    setChatWidgetStatus: simpleSetPayload("chatWidgetStatus"),
    chatUpdateStudentStatus: chatStudentsAdapter.updateOne,
    chatSetStudentsStatus: chatStudentsAdapter.setAll,
    chatUpdateAllStudents: chatStudentsAdapter.updateMany,
  },
  extraReducers: {
    [setChatWidgetStatusAndUpdateReadTime.fulfilled]: (state, action) => {
      state.chatWidgetStatus = action?.payload;
    },
  },
});

export default chatSlice.reducer;

//actions
export const { chatUpdateStudentStatus, chatSetStudentsStatus, chatUpdateAllStudents, setChatMessageHandler,
  setMessageList, setNumUnreadMessages, setInitialMessageList, setChatWidgetStatus } = chatSlice.actions;

export const setTherapistChatWidgetStatusAndUpdateReadTime = createAsyncThunk(
  `${SLICE_NAMES.CHAT_SLICE}/setTherapistChatWidgetStatusAndUpdateReadTime`,
  async (payload, thunkAPI) => {
    const { dispatch } = thunkAPI;
    const { sentTo, sentBy, treatmentId, chatWidgetStatus } = payload;
  
    dispatch(chatUpdateStudentStatus({ id: sentBy, changes: { numUnreadMessages: 0, chatWidgetStatus } }));
  
    const data = { sent_to: sentTo,
      sent_by: sentBy };
  
    updateChatReadMessages(treatmentId, data);
  
    return chatWidgetStatus;
  },
);

//selectors
const selectChatMessageHandler = createSimpleSelector("chatMessageHandler");
const selectMessageList = createSimpleSelector("messageList");
const selectNumUnreadMessages = createSimpleSelector("numUnreadMessages");
const selectMessageDraft = createSimpleSelector("messageDraft");
const selectChatWidgetStatus = createSimpleSelector("chatWidgetStatus");

export const { selectAll: chatSelectAllStudentsStatus, selectIds: chatSelectStudentStatusIds,
  selectById: chatSelectStudentStatusById, selectEntities: chatSelectAllStudentStatusDict } = chatStudentsAdapterSelectors;
export { selectChatMessageHandler, selectMessageList, selectNumUnreadMessages, selectMessageDraft, selectChatWidgetStatus };
